import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api.service';

function registerLinkLabel(r: { type?: string; name?: string; code?: string }) {
  const type =
    r.type === 'CENTRAL_TREASURY' ? 'Caja Central' : r.type === 'BRANCH_MASTER' ? 'Caja Madre' : r.type;
  return `${type} ${r.name || r.code || ''}`.trim();
}

export function BankReconciliation() {
  const [branches, setBranches] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [periodKey, setPeriodKey] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [periodView, setPeriodView] = useState<any | null>(null);
  const [unmatchedSystem, setUnmatchedSystem] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [summary, setSummary] = useState<any | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [matchModal, setMatchModal] = useState<{ movementId: string; candidates: any[] } | null>(null);

  const [accountForm, setAccountForm] = useState({
    branchId: '',
    bankName: '',
    templateCode: 'GENERIC_AR',
    cbu: '',
    alias: '',
    accountNumber: '',
  });

  const loadAccounts = async () => {
    const [bs, ts, accs] = await Promise.all([
      apiService.getBranches(),
      apiService.listBankImportTemplates(),
      apiService.listBankAccounts(),
    ]);
    setBranches(bs);
    setTemplates(ts);
    setAccounts(accs);
    if (!selectedAccountId && accs.length > 0) setSelectedAccountId(accs[0].id);
  };

  const loadMovements = async (accountId: string) => {
    if (!accountId || !periodKey) return;
    const [from, to] = (() => {
      const [y, m] = periodKey.split('-').map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0, 23, 59, 59, 999);
      return [start.toISOString(), end.toISOString()];
    })();
    const [movs, period, unmatched] = await Promise.all([
      apiService.listBankMovements(accountId, {
        from,
        to,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
      apiService.getBankReconciliationPeriod(accountId, periodKey),
      apiService.listBankUnmatchedSystem(accountId, periodKey),
    ]);
    setMovements(movs);
    setPeriodView(period);
    setSummary({
      total: (period.kpis?.matched ?? 0) + (period.kpis?.unmatchedExtract ?? 0) + (period.kpis?.ignored ?? 0),
      matched: period.kpis?.matched ?? 0,
      unmatched: period.kpis?.unmatchedExtract ?? 0,
      totalCredits: period.kpis?.totalCredits ?? 0,
      totalDebits: period.kpis?.totalDebits ?? 0,
    });
    setUnmatchedSystem(unmatched.items ?? []);
  };

  useEffect(() => {
    loadAccounts().catch((e: any) => setError(e?.response?.data?.message ?? e.message));
  }, []);

  useEffect(() => {
    if (selectedAccountId) {
      loadMovements(selectedAccountId).catch((e: any) => setError(e?.response?.data?.message ?? e.message));
    }
  }, [selectedAccountId, statusFilter, periodKey]);

  const periodClosed = periodView?.reconciliation?.status === 'COMPLETED';

  const createAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiService.createBankAccount({
        branchId: accountForm.branchId,
        bankName: accountForm.bankName,
        templateCode: accountForm.templateCode,
        cbu: accountForm.cbu || undefined,
        alias: accountForm.alias || undefined,
        accountNumber: accountForm.accountNumber || undefined,
      });
      setShowAccountForm(false);
      await loadAccounts();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const importStatement = async () => {
    if (!selectedAccountId || !importFile) return;
    setBusy(true);
    setError(null);
    try {
      const acc = accounts.find((a) => a.id === selectedAccountId);
      await apiService.importBankStatement(selectedAccountId, importFile, acc?.templateCode);
      setImportFile(null);
      await loadMovements(selectedAccountId);
      await loadAccounts();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const runAutoMatch = async () => {
    if (!selectedAccountId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiService.autoMatchBankMovements(selectedAccountId, periodKey);
      alert(`Conciliados: ${r.matched}, omitidos: ${r.skipped}`);
      await loadMovements(selectedAccountId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const completePeriod = async () => {
    if (!selectedAccountId || !periodKey) return;
    if (!window.confirm('¿Cerrar la conciliación de este mes?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.completeBankReconciliationPeriod(selectedAccountId, periodKey);
      await loadMovements(selectedAccountId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const reopenPeriod = async () => {
    if (!selectedAccountId || !periodKey) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.reopenBankReconciliationPeriod(selectedAccountId, periodKey);
      await loadMovements(selectedAccountId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const openMatch = async (movementId: string) => {
    setBusy(true);
    try {
      const candidates = await apiService.listBankMatchCandidates(selectedAccountId, movementId);
      setMatchModal({ movementId, candidates });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmMatch = async (lineId: string, paymentId: string) => {
    if (!matchModal) return;
    setBusy(true);
    try {
      await apiService.manualBankMatch({
        bankMovementId: matchModal.movementId,
        paymentMethodLineId: lineId,
        paymentId,
      });
      setMatchModal(null);
      await loadMovements(selectedAccountId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);
  const linkedRegisters = (selectedAccount?.cashRegisters ?? [])
    .map((l: any) => l.cashRegister)
    .filter(Boolean);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Conciliación bancaria</h1>
          <p className="text-gray-600 mt-1">
            Compará el extracto bancario contra los movimientos registrados.
          </p>
        </div>
        <button
          onClick={() => setShowAccountForm((v) => !v)}
          className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 text-sm"
        >
          {showAccountForm ? 'Cancelar' : '+ Cuenta bancaria'}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {showAccountForm && (
        <form onSubmit={createAccount} className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
            <select required value={accountForm.branchId} onChange={(e) => setAccountForm({ ...accountForm, branchId: e.target.value })} className="w-full border rounded-md px-3 py-2">
              <option value="">Seleccionar…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
            <input required value={accountForm.bankName} onChange={(e) => setAccountForm({ ...accountForm, bankName: e.target.value })} className="w-full border rounded-md px-3 py-2" placeholder="Ej. Galicia" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plantilla importación</label>
            <select value={accountForm.templateCode} onChange={(e) => setAccountForm({ ...accountForm, templateCode: e.target.value })} className="w-full border rounded-md px-3 py-2">
              {templates.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CBU</label>
            <input value={accountForm.cbu} onChange={(e) => setAccountForm({ ...accountForm, cbu: e.target.value })} className="w-full border rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
            <input value={accountForm.alias} onChange={(e) => setAccountForm({ ...accountForm, alias: e.target.value })} className="w-full border rounded-md px-3 py-2" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy} className="px-4 py-2 bg-slate-900 text-white rounded-md disabled:opacity-50">Crear cuenta</button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <div className="min-w-[220px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Cuenta bancaria</label>
          <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)} className="w-full border rounded-md px-3 py-2">
            <option value="">Seleccionar…</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.branch?.name} — {a.bankName} {a.cbu ? `(${a.cbu.slice(-4)})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
          <input
            type="month"
            value={periodKey}
            onChange={(e) => setPeriodKey(e.target.value)}
            className="border rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estado líneas</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded-md px-3 py-2">
            <option value="">Todos</option>
            <option value="UNMATCHED">Pendientes</option>
            <option value="MATCHED">Conciliados</option>
            <option value="IGNORED">Ignorados</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Importar extracto</label>
          <input type="file" accept=".csv,.xls,.xlsx,.txt" onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} className="text-sm" disabled={periodClosed} />
        </div>
        <button onClick={importStatement} disabled={busy || !importFile || !selectedAccountId || periodClosed} className="px-4 py-2 border border-slate-300 bg-white text-slate-800 rounded-md text-sm disabled:opacity-50 hover:bg-slate-50">
          Subir extracto
        </button>
        <button onClick={runAutoMatch} disabled={busy || !selectedAccountId || periodClosed} className="px-4 py-2 border border-slate-300 bg-white text-slate-800 rounded-md text-sm disabled:opacity-50 hover:bg-slate-50">
          Auto-conciliar mes
        </button>
        {!periodClosed ? (
          <button onClick={completePeriod} disabled={busy || !selectedAccountId} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50">
            Cerrar mes
          </button>
        ) : (
          <button onClick={reopenPeriod} disabled={busy || !selectedAccountId} className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm disabled:opacity-50">
            Reabrir mes
          </button>
        )}
      </div>

      {periodView && selectedAccount && (
        <div className="bg-white border rounded-lg p-3 text-sm text-gray-700 flex flex-wrap gap-4 items-center">
          <span>
            Período <strong>{periodKey}</strong> · estado{' '}
            <strong>{periodView.reconciliation?.status ?? '—'}</strong>
          </span>
          {periodView.kpis?.balanceDiff != null && (
            <span>
              Diff saldo:{' '}
              <strong>${Number(periodView.kpis.balanceDiff).toLocaleString('es-AR')}</strong>
            </span>
          )}
        </div>
      )}

      {selectedAccount && (
        <div className="bg-white border rounded-lg p-4 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="font-medium text-gray-900">Vínculo con tesorería</h2>
              <p className="text-xs text-gray-500">
                Solo lectura. Los depósitos y extracciones se registran en Tesorería.
              </p>
            </div>
            <Link
              to={`/treasury/banks?account=${encodeURIComponent(selectedAccountId)}&link=1`}
              className="text-sm text-blue-700 hover:underline"
            >
              Administrar vínculo en Tesorería
            </Link>
          </div>
          {linkedRegisters.length === 0 ? (
            <p className="text-sm text-amber-800">Sin vincular a Central / Madre.</p>
          ) : (
            <ul className="text-sm text-gray-800 space-y-1">
              {linkedRegisters.map((r: any) => (
                <li key={r.id}>
                  Vinculada a <strong>{registerLinkLabel(r)}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {summary && selectedAccount && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            ['Total extracto', summary.total],
            ['Conciliados', summary.matched],
            ['Pendientes extracto', summary.unmatched],
            ['Sin match sistema', periodView?.kpis?.unmatchedSystem ?? unmatchedSystem.length],
            ['Ingresos', `$${Number(summary.totalCredits).toLocaleString()}`],
            ['Egresos', `$${Number(summary.totalDebits).toLocaleString()}`],
          ].map(([label, val]) => (
            <div key={String(label)} className="bg-white border rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500 uppercase">{label}</div>
              <div className="text-lg font-semibold">{val}</div>
            </div>
          ))}
        </div>
      )}

      {selectedAccount && unmatchedSystem.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="font-medium text-gray-900">Sistema sin match ({unmatchedSystem.length})</h2>
            <p className="text-xs text-gray-500">Ledger, pagos, proveedores y gastos del mes sin conciliar.</p>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {unmatchedSystem.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {row.date ? String(row.date).slice(0, 10) : '—'}
                  </td>
                  <td className="px-4 py-2">{row.kind}</td>
                  <td className="px-4 py-2 max-w-md truncate" title={row.label}>{row.label}</td>
                  <td className="px-4 py-2 text-right">${Number(row.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedAccount && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Débito</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Crédito</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {movements.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Sin movimientos. Importá un extracto CSV o Excel.</td></tr>
              ) : movements.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">{m.movementDate?.slice?.(0, 10)}</td>
                  <td className="px-4 py-2 max-w-xs truncate" title={m.description}>{m.description ?? '—'}</td>
                  <td className="px-4 py-2 text-right text-red-700">{m.debit > 0 ? `$${m.debit.toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-2 text-right text-green-700">{m.credit > 0 ? `$${m.credit.toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      m.matchStatus === 'MATCHED' ? 'bg-green-100 text-green-800' :
                      m.matchStatus === 'IGNORED' ? 'bg-gray-100 text-gray-600' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>{m.matchStatus}</span>
                    {m.match?.payment && (
                      <div className="text-xs text-gray-500 mt-1">
                        {m.match.payment.customer?.firstName} {m.match.payment.customer?.lastName}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!periodClosed && m.matchStatus === 'UNMATCHED' && (
                      <>
                        <button onClick={() => openMatch(m.id)} className="text-blue-700 hover:underline mr-2">Conciliar</button>
                        <button onClick={() => apiService.ignoreBankMovement(m.id).then(() => loadMovements(selectedAccountId))} className="text-gray-600 hover:underline">Ignorar</button>
                      </>
                    )}
                    {!periodClosed && m.matchStatus === 'MATCHED' && (
                      <button onClick={() => apiService.unmatchBankMovement(m.id).then(() => loadMovements(selectedAccountId))} className="text-orange-700 hover:underline">Deshacer</button>
                    )}
                    {periodClosed && <span className="text-gray-400 text-xs">Cerrado</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {matchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Conciliar manualmente</h3>
            {matchModal.candidates.length === 0 ? (
              <p className="text-sm text-gray-500 mb-4">No hay transferencias candidatas (mismo monto ±3 días, misma sucursal).</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {matchModal.candidates.map((c) => (
                  <li key={c.id} className="border rounded p-3 flex justify-between items-center">
                    <div className="text-sm">
                      <div className="font-medium">${c.amount.toLocaleString()} — {c.payment?.customer?.firstName} {c.payment?.customer?.lastName}</div>
                      <div className="text-gray-500 text-xs">{c.payment?.paymentDate?.slice?.(0, 10)} · #{c.payment?.loan?.loanNumber} · {c.transactionNumber ?? 'sin nro'}</div>
                    </div>
                    <button onClick={() => confirmMatch(c.id, c.paymentId)} disabled={busy} className="text-blue-700 text-sm hover:underline">Vincular</button>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => setMatchModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}
